/**
 * `/v1/book` in local mode (ADR-0042 · #84).
 *
 * Local mode has no operator and no CSV, but it must still serve a book, or the
 * E2E suite could not run the same specs against both data sources — the rule
 * that makes backend drift turn E2E red (plan §1.1 rule 5). `source` reports
 * `synthetic` so the SPA can never present this as somebody's real holdings.
 *
 * Determinism WITHOUT the random kernel, on purpose. The obvious move is a
 * fifth seeded stream, but plan §0.3 fixes exactly four seed suffixes
 * (`ticker+'v3'`, `ticker+win+'s'`, `ticker+'a'`, `ticker+'n'`) and the
 * seed-string assertion enumerates them verbatim as the single highest-value
 * test in the suite. A book does not need randomness to be plausible, so this
 * derives everything from the row's ORDINAL in the frozen universe: no new
 * seed, no reachable interaction with the golden digest, and re-derivable by
 * reading twenty lines instead of replaying a PRNG.
 */
import type { BookLot, BookPayload } from '@meridian/contracts';

import type { DerivedSecurity } from '../../universe/derive-security';

/**
 * Every 7th name. Prime and coprime with the 11 sectors, so the sample does not
 * land disproportionately in one sector and make the synthetic book look like a
 * deliberately concentrated portfolio.
 *
 * Sized against the SYNTHETIC universe (129 rows → ~19 lots), not the ~560-name
 * live one. A book of five names leaves the DJI intersection at zero or one, and
 * a book filter that composes with an index filter over one row is a test that
 * passes without asserting anything.
 */
const SYNTHETIC_BOOK_STRIDE = 7;

/** Lot sizes cycle 10…190 rather than sitting flat, so weight columns vary. */
const SYNTHETIC_QTY_STEP = 10;
const SYNTHETIC_QTY_CYCLE = 19;

/**
 * Every third lot has no cost. The optional-cost path (#84) is otherwise never
 * rendered in local mode, and "renders fine until a real CSV omits a cost" is
 * precisely the defect a synthetic fixture exists to prevent.
 */
const SYNTHETIC_UNCOSTED_EVERY = 3;

export function buildSyntheticBook(rows: readonly DerivedSecurity[]): BookPayload {
  /* `filter` then `map`, not an index loop: `core` forbids loops, `let` and
     array mutation (style §4), and the two-step form drops the
     `noUncheckedIndexedAccess` guard the indexed version needed. `ordinal` is
     the position WITHIN the book, which is what the qty and cost cycles key
     off — so the book truncates predictably when the row set does. */
  const lots: readonly BookLot[] = rows
    .filter((_row, index) => index % SYNTHETIC_BOOK_STRIDE === 0)
    .map((row, ordinal) => ({
      ticker: row.ticker,
      qty: SYNTHETIC_QTY_STEP * (1 + (ordinal % SYNTHETIC_QTY_CYCLE)),
      cost: ordinal % SYNTHETIC_UNCOSTED_EVERY === 0 ? null : roundedCost(row),
    }));
  /* No warnings: a generated fixture has no operator to warn. */
  return { source: 'synthetic', lots: [...lots], warnings: [] };
}

/**
 * A cost basis near — but never equal to — the current price, so a P/L column
 * built on this fixture shows both signs. Two decimals because a cost basis is
 * money the operator typed, not a derived float.
 */
function roundedCost(row: DerivedSecurity): number {
  const drift = 0.85 + ((row.ticker.length * 7) % 30) / 100;
  return Math.max(0.01, Math.round(row.price * drift * 100) / 100);
}
